import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_STATUSES } from "../../../utils/constants";
import { StatusInterface } from "../../../models/TicketModule/Status";
import statusService from "../../../services/ticketService/statusService";

interface UpdateStatusesContext {
  previousStatuses: StatusInterface[];
}

const useUpdateStatus = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    StatusInterface,
    Error,
    StatusInterface,
    UpdateStatusesContext
  >({
    mutationFn: async (status: StatusInterface) => {
      const { id, ...statusData } = status;
      return statusService.Update(id || "", statusData);
    },

    onMutate: async (updatedStatus: StatusInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousStatuses =
        queryClient.getQueryData<StatusInterface[]>([
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<StatusInterface[]>(
        [
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (statuses = []) =>
          statuses.map((status) =>
            status.id === updatedStatus.id ? updatedStatus : status
          )
      );

      return { previousStatuses };
    },

    onSuccess: (savedStatus) => {
      queryClient.setQueryData<StatusInterface[]>(
        [
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (statuses) =>
          statuses?.map((status) =>
            status.id === savedStatus.id ? savedStatus : status
          )
      );
    },

    onError: (error, updatedStatus, context) => {
      if (!context) return;

      queryClient.setQueryData<StatusInterface[]>(
        [
          CACHE_KEY_STATUSES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousStatuses
      );
    },
  });
};

export default useUpdateStatus;
